import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { DEFAULT_VALUE_0 } from 'common/config';
import { AppDispatch, RootState } from 'app/store';
import {
  IRfp,
  IChangeRfpActiveUntilPayload,
  IChangeRfpStatusPayload,
  IDeleteRfpPayload,
  IRfpItem,
  ICreateRfpItemPayload,
  IDeleteRfpItemPayload,
  IRfpItemGoods,
  ICreateRfpItemGoodsPayload,
  IDeleteRfpItemGoodsPayload,
  IUpdateRfpItemGoodsPayload,
  IUpdateRfpItemPayload,
  IRfpListParams,
  IRfpStats,
  IUpdateRfpPayload,
} from 'entities/Rfp/Rfp.models';
import { rfpTransport } from 'entities/Rfp/Rfp.transport';

interface IState {
  rfp: IRfp | null;
  rfpList: IRfp[];
  rfpCount: number;
  isRfpLoading: boolean;
  rfpStats: IRfpStats;
}

const initialState: IState = {
  rfp: null,
  rfpList: [],
  rfpCount: 0,
  isRfpLoading: false,
  rfpStats: {
    activeCount: DEFAULT_VALUE_0,
    draftCount: DEFAULT_VALUE_0,
  },
};

export const rfpSlice = createSlice({
  name: 'rfp',
  initialState,
  reducers: {
    setRfp(state, action: PayloadAction<IRfp | null>) {
      state.rfp = action.payload;
    },
    setRfpList(state, action: PayloadAction<IRfp[]>) {
      state.rfpList = action.payload;
    },
    setRfpCount(state, action: PayloadAction<number>) {
      state.rfpCount = action.payload;
    },
    setIsRfpLoading(state, action: PayloadAction<boolean>) {
      state.isRfpLoading = action.payload;
    },
    setRfpStats(state, action: PayloadAction<IRfpStats>) {
      state.rfpStats = action.payload;
    },
    addRfpListItems(state, action: PayloadAction<IRfp[]>) {
      state.rfpList.push(...action.payload);
    },
    filterRfpListItems(state, action: PayloadAction<IRfp[]>) {
      const rfpIds = action.payload.map((item) => item.id);

      state.rfpList = state.rfpList.filter((item) => !rfpIds.includes(item.id));
    },
    addRfpItem(state, action: PayloadAction<IRfpItem>) {
      if (!state.rfp) {
        return;
      }

      state.rfp.items.push(action.payload);
    },
    patchRfpItem(state, action: PayloadAction<IRfpItem>) {
      if (!state.rfp) {
        return;
      }

      const item = state.rfp.items.find((i) => i.id === action.payload.id);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    removeRfpItem(state, action: PayloadAction<number>) {
      if (!state.rfp) {
        return;
      }

      state.rfp.items = state.rfp.items.filter((item) => item.id !== action.payload);
    },
    addRfpItemGood(state, action: PayloadAction<IRfpItemGoods>) {
      if (!state.rfp) {
        return;
      }

      const item = state.rfp.items.find((i) => i.id === action.payload.rfpItemId);

      if (!item) {
        return;
      }

      item.goods.push(action.payload);
    },
    patchRfpItemGood(state, action: PayloadAction<IRfpItemGoods>) {
      if (!state.rfp) {
        return;
      }

      const item = state.rfp.items.find((i) => i.id === action.payload.rfpItemId);

      if (!item) {
        return;
      }

      const good = item.goods.find((i) => i.id === action.payload.id);

      if (!good) {
        return;
      }

      Object.assign(good, action.payload);
    },
    removeRfpItemGood(state, action: PayloadAction<IRfpItemGoods>) {
      if (!state.rfp) {
        return;
      }

      const item = state.rfp.items.find((i) => i.id === action.payload.rfpItemId);

      if (!item) {
        return;
      }

      item.goods = item.goods.filter((i) => i.id !== action.payload.id);
    },
  },
});

export const {
  setRfp,
  setRfpList,
  setRfpCount,
  setIsRfpLoading,
  setRfpStats,
  addRfpListItems,
  filterRfpListItems,
  addRfpItem,
  patchRfpItem,
  removeRfpItem,
  addRfpItemGood,
  patchRfpItemGood,
  removeRfpItemGood,
} = rfpSlice.actions;

export const selectRfp = (state: RootState) => state.rfp.rfp;
export const selectRfpList = (state: RootState) => state.rfp.rfpList;
export const selectRfpCount = (state: RootState) => state.rfp.rfpCount;
export const selectIsRfpLoading = (state: RootState) => state.rfp.isRfpLoading;
export const selectRfpStats = (state: RootState) => state.rfp.rfpStats;

export const rfpReducer = rfpSlice.reducer;

export const getRfpList = (params: IRfpListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.get(params);
    dispatch(setRfpList(response.data));
    dispatch(setRfpCount(response.count));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const getRfpListChunk = (params: IRfpListParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.get(params);
    dispatch(addRfpListItems(response.data));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const getRfpStats = () => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.getStats();
    dispatch(setRfpStats(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const getRfpById = (id: number) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.getById(id);
    dispatch(setRfp(response));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const createRfp = () => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.create();
    dispatch(setRfp(response));
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const updateRfp = (payload: IUpdateRfpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.update(payload);
    dispatch(setRfp(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const deleteRfp = (payload: IDeleteRfpPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.delete(payload);
    dispatch(filterRfpListItems([response]));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const changeRfpStatus = (payload: IChangeRfpStatusPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.changeStatus(payload);
    dispatch(setRfp(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const changeRfpActiveUntil = (payload: IChangeRfpActiveUntilPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.changeActiveUntil(payload);
    dispatch(setRfp(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const createRfpItem = (payload: ICreateRfpItemPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.createItem(payload);
    dispatch(addRfpItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const updateRfpItem = (payload: IUpdateRfpItemPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.updateItem(payload);
    dispatch(patchRfpItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const deleteRfpItem = (payload: IDeleteRfpItemPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.deleteItem(payload);
    dispatch(removeRfpItem(response.id));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const createRfpItemGoods = (payload: ICreateRfpItemGoodsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.createItemGoods(payload);
    dispatch(addRfpItemGood(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const updateRfpItemGoods = (payload: IUpdateRfpItemGoodsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.updateItemGoods(payload);
    dispatch(patchRfpItemGood(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};

export const deleteRfpItemGoods = (payload: IDeleteRfpItemGoodsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsRfpLoading(true));

  try {
    const response = await rfpTransport.deleteItemGoods(payload);
    dispatch(removeRfpItemGood(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsRfpLoading(false));
  }
};
